import React, { useState, useEffect } from 'react';
import { Download, Smartphone, X, Check, Share, PlusSquare, Monitor, HelpCircle } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAInstallPromptProps {
  isSuppressed?: boolean;
}

export function PWAInstallPrompt({ isSuppressed = false }: PWAInstallPromptProps) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    try {
      return typeof window !== 'undefined' && !!localStorage.getItem('azuraos_pwa_dismissed');
    } catch {
      return false;
    }
  });
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [showDesktopGuide, setShowDesktopGuide] = useState(false);
  const [installedSuccess, setInstalledSuccess] = useState(false);

  useEffect(() => {
    // Check if app is already running as standalone PWA
    const checkStandalone = () => {
      const isStandaloneMode =
        window.matchMedia('(display-mode: standalone)').matches ||
        (navigator as any).standalone ||
        document.referrer.includes('android-app://');
      setIsStandalone(isStandaloneMode);
    };

    checkStandalone();

    // Check iOS Safari
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(isIosDevice);

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setInstalledSuccess(true);
      setDeferredPrompt(null);
      setTimeout(() => setIsStandalone(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      if (choiceResult.outcome === 'accepted') {
        setInstalledSuccess(true);
      }
      setDeferredPrompt(null);
    } else if (isIOS) {
      setShowIOSGuide(true);
    } else {
      setShowDesktopGuide(true);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('azuraos_pwa_dismissed', 'true');
      }
    } catch {}
  };

  if (isStandalone || isDismissed || isSuppressed) {
    return null;
  }

  // If installed successfully
  if (installedSuccess) {
    return (
      <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-6 sm:w-96 z-50 bg-[#151819] border border-[#14B8A6]/40 p-4 rounded-2xl shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-300">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#14B8A6]/20 border border-[#14B8A6]/40 text-[#14B8A6] flex items-center justify-center shrink-0">
            <Check size={20} />
          </div>
          <div>
            <div className="text-xs font-bold text-white">AzuraOS Installed!</div>
            <div className="text-[11px] text-gray-400">You can now launch AzuraOS directly from your home screen.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Floating PWA Banner */}
      <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-6 sm:w-96 z-50 bg-[#151819]/95 backdrop-blur-md border border-[#2A2F31] hover:border-[#14B8A6]/40 p-4 rounded-2xl shadow-2xl transition-all animate-in fade-in slide-in-from-bottom-4 duration-300 group text-white">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#14B8A6]/10 border border-[#14B8A6]/30 text-[#14B8A6] flex items-center justify-center shrink-0 mt-0.5">
              <Smartphone size={20} />
            </div>
            <div>
              <div className="text-xs font-bold text-white flex items-center gap-1.5">
                <span>Install AzuraOS App</span>
                <span className="px-1.5 py-0.5 rounded bg-[#14B8A6]/20 text-[#14B8A6] text-[9px] font-extrabold uppercase">
                  PWA
                </span>
              </div>
              <p className="text-[11px] text-gray-400 mt-0.5 leading-relaxed">
                Add AzuraOS to your home screen or desktop for full-screen experience & instant offline access.
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-gray-500 hover:text-white p-1 rounded-lg hover:bg-[#2A2F31] transition-all shrink-0 cursor-pointer"
            title="Dismiss"
          >
            <X size={14} />
          </button>
        </div>

        <div className="mt-3.5 flex items-center gap-2">
          <button
            onClick={handleInstallClick}
            className="flex-1 py-2 px-3 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold text-xs rounded-xl shadow-lg hover:shadow-[#14B8A6]/20 transition-all flex items-center justify-center gap-1.5 cursor-pointer active:scale-95"
          >
            <Download size={14} />
            <span>{isIOS ? 'Add to Home Screen' : 'Install App'}</span>
          </button>
          <button
            onClick={() => setIsDismissed(true)}
            className="py-2 px-3 bg-[#1F2426] hover:bg-[#2A2F31] text-gray-300 font-medium text-xs rounded-xl transition-all cursor-pointer"
          >
            Not Now
          </button>
        </div>
      </div>

      {/* iOS Guide Modal */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#151819] border border-[#2A2F31] max-w-sm w-full rounded-2xl p-5 shadow-2xl text-white animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-4 border-b border-[#2A2F31] pb-3">
              <div className="flex items-center gap-2">
                <Smartphone className="text-[#14B8A6]" size={18} />
                <h3 className="text-sm font-bold">Install on iPhone / iPad</h3>
              </div>
              <button
                onClick={() => setShowIOSGuide(false)}
                className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-[#2A2F31]"
              >
                <X size={16} />
              </button>
            </div>

            <div className="space-y-4 text-xs text-gray-300">
              <div className="flex items-start gap-3 bg-[#0C0E0F] p-3 rounded-xl border border-[#2A2F31]">
                <div className="w-6 h-6 rounded-full bg-[#14B8A6]/20 text-[#14B8A6] flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  1
                </div>
                <div>
                  <span className="text-white font-semibold flex items-center gap-1">
                    Tap the Share button <Share size={13} className="text-[#14B8A6]" />
                  </span>
                  <span className="text-[11px] text-gray-400 block mt-0.5">
                    Located at the bottom of your Safari browser bar.
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-[#0C0E0F] p-3 rounded-xl border border-[#2A2F31]">
                <div className="w-6 h-6 rounded-full bg-[#14B8A6]/20 text-[#14B8A6] flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  2
                </div>
                <div>
                  <span className="text-white font-semibold flex items-center gap-1">
                    Select "Add to Home Screen" <PlusSquare size={13} className="text-[#14B8A6]" />
                  </span>
                  <span className="text-[11px] text-gray-400 block mt-0.5">
                    Scroll down in the action menu and tap 'Add to Home Screen'.
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-[#0C0E0F] p-3 rounded-xl border border-[#2A2F31]">
                <div className="w-6 h-6 rounded-full bg-[#14B8A6]/20 text-[#14B8A6] flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  3
                </div>
                <div>
                  <span className="text-white font-semibold">Tap "Add" in top right</span>
                  <span className="text-[11px] text-gray-400 block mt-0.5">
                    AzuraOS icon will be created on your home screen for instant launch.
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowIOSGuide(false)}
              className="w-full mt-5 py-2.5 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold text-xs rounded-xl transition-all cursor-pointer"
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {/* Desktop / Browser Guide Modal */}
      {showDesktopGuide && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#151819] border border-[#2A2F31] max-w-sm w-full rounded-2xl p-5 shadow-2xl text-white animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-4 border-b border-[#2A2F31] pb-3">
              <div className="flex items-center gap-2">
                <Monitor className="text-[#14B8A6]" size={18} />
                <h3 className="text-sm font-bold">Install AzuraOS Desktop / Android App</h3>
              </div>
              <button
                onClick={() => setShowDesktopGuide(false)}
                className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-[#2A2F31]"
              >
                <X size={16} />
              </button>
            </div>

            <div className="space-y-4 text-xs text-gray-300">
              <div className="flex items-start gap-3 bg-[#0C0E0F] p-3 rounded-xl border border-[#2A2F31]">
                <div className="w-6 h-6 rounded-full bg-[#14B8A6]/20 text-[#14B8A6] flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  1
                </div>
                <div>
                  <span className="text-white font-semibold">Check Address Bar Icon</span>
                  <span className="text-[11px] text-gray-400 block mt-0.5">
                    Click the <strong>Install App (⊕)</strong> icon on the right side of your browser URL bar.
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-[#0C0E0F] p-3 rounded-xl border border-[#2A2F31]">
                <div className="w-6 h-6 rounded-full bg-[#14B8A6]/20 text-[#14B8A6] flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  2
                </div>
                <div>
                  <span className="text-white font-semibold">Or Browser Menu (3 Dots)</span>
                  <span className="text-[11px] text-gray-400 block mt-0.5">
                    Click the top-right browser menu (⋮) → select <strong>Install AzuraOS</strong> or <strong>Save and Share &gt; Install App / Create Shortcut</strong>.
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-[#0C0E0F] p-3 rounded-xl border border-[#2A2F31]">
                <div className="w-6 h-6 rounded-full bg-[#14B8A6]/20 text-[#14B8A6] flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  3
                </div>
                <div>
                  <span className="text-white font-semibold">Standalone Launch</span>
                  <span className="text-[11px] text-gray-400 block mt-0.5">
                    AzuraOS will launch as a dedicated native window without browser tabs!
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowDesktopGuide(false)}
              className="w-full mt-5 py-2.5 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold text-xs rounded-xl transition-all cursor-pointer"
            >
              Understood
            </button>
          </div>
        </div>
      )}
    </>
  );
}
